// Face sign-in (/face-id; jobq-api "face sign-in", contract 2026-10-15).
//
// The three station Mac minis (laser, brake, blast/fab/welding) each have a webcam. When JobQ asks
// for a PIN there, the camera recognises the operator (utils/pinPrompt.js + src/face/). This page:
//   - This computer: make this browser a station (an Admin PIN, POST /face/stations; the key is
//     kept in this browser only), its status, a camera preview and "Test sign-in", and "Stop using".
//   - Enroll a person / Remove my face (on a station only): the person types their OWN PIN.
//   - Admin: who is enrolled, the stations, the last 7 days (the admin session's X-Admin-Pin).
// The page itself is NOT behind the admin unlock: people enroll themselves with their own PIN, and
// a station tab left in an admin session would autofill the Admin PIN instead of using face.

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import { AdminContext, requestAdminPin } from '../adminSession.jsx';
import pinPrompt from '../utils/pinPrompt';
import { adminPinFromSession, needsAdminPin } from '../utils/securityApi';
import { clearStation, getStationKey, getStationName, getStationStatus, setStation } from '../face/faceStation';
import { enrollFace, identifyFace, readIdentify, removeMyFace } from '../face/faceApi';
import {
  createStation, faceSummary, listEnrollments, listStations, removeEnrollment, revokeStation,
} from '../face/faceAdminApi';
import { acquireCamera, captureJpeg, lastCameraError, releaseCamera, stopCamera } from '../face/faceCamera';
import FacePreview from '../face/FacePreview';

export const FACE_NOTICE = 'Face sign-in: when JobQ asks who you are, this camera takes one picture and checks it against the '
  + 'faces enrolled here. Pictures are not saved; only a face measurement is stored, and it is used for '
  + 'sign-in only, never to track or time anyone. You can always type your PIN instead, and you can remove '
  + 'your face here at any time.';

export const STATION_SUGGESTIONS = ['Laser', 'Brake', 'Blast-Fab-Weld'];
export const ENROLL_FRAMES = 5;
export const ENROLL_GAP_MS = 400;

const OUTCOME_LABELS = {
  matched: 'Matched',
  no_face: 'No face',
  multiple_faces: 'Several people',
  no_match: 'No match',
  ambiguous: 'Unsure',
  disabled: 'Switched off',
  unavailable: 'Unavailable',
};

const styles = {
  page: { maxWidth: 980 },
  card: { marginBottom: 16 },
  row: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' },
  col: { flex: '1 1 280px', minWidth: 0 },
  notice: { fontSize: '0.9rem', background: 'rgba(13,110,253,0.06)', borderLeft: '3px solid #0d6efd', padding: '8px 12px', marginBottom: 12 },
  pin: { maxWidth: 180 },
  muted: { color: '#6c757d', fontSize: '0.875rem' },
  small: { fontSize: '0.85rem' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function stamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function similarityText(sim) {
  if (typeof sim !== 'number') return '';
  return `similarity ${Math.round(sim * 10) / 10}`;
}

export function outcomeLine(byOutcome) {
  if (!byOutcome || typeof byOutcome !== 'object') return '';
  return Object.entries(byOutcome)
    .filter(([, n]) => Number(n) > 0)
    .map(([k, n]) => `${OUTCOME_LABELS[k] || k} ${n}`)
    .join(' · ');
}

/** The station webcam while this page shows a station (released when the page closes). */
function useStationCamera(on) {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!on) {
      setStream(null);
      return undefined;
    }
    let gone = false;
    acquireCamera().then((s) => {
      if (gone) return;
      setStream(s);
      setError(s ? null : lastCameraError());
    });
    return () => {
      gone = true;
      releaseCamera();
    };
  }, [on]);
  return { stream, error };
}

function ThisComputerCard({ stationKey, status, stream, cameraError, onChanged, revokedNote }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [test, setTest] = useState(null);

  const setUp = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setMsg(null);
    // The admin session's PIN when there is one; else a PIN for this one call (the tab is not unlocked).
    let typed = null;
    if (!adminPinFromSession()) {
      typed = await pinPrompt(`Admin PIN to make this computer the "${n}" face sign-in station:`, { autofill: false, face: false });
      if (!typed) return;
    }
    setBusy(true);
    const r = await createStation(n, { adminPin: typed });
    setBusy(false);
    if (!r.ok || !r.body || !r.body.station_key) {
      setMsg({ kind: 'danger', text: needsAdminPin(r) ? `That needs an active Admin's PIN. ${r.message || ''}` : (r.message || 'JobQ did not answer.') });
      return;
    }
    if (!setStation(r.body.station_key, r.body.name || n)) {
      setMsg({ kind: 'danger', text: 'This browser would not keep the station key (a private window?). Ask an Admin to revoke the new station and try in a normal window.' });
      return;
    }
    setName('');
    setMsg({ kind: 'success', text: `This computer is now the "${r.body.name || n}" face sign-in station.` });
    onChanged();
  };

  const runTest = async () => {
    setBusy(true);
    setTest(null);
    const image = await captureJpeg();
    if (!image) {
      setBusy(false);
      setTest({ kind: 'warning', text: lastCameraError() || 'The camera gave no picture.' });
      return;
    }
    const r = await identifyFace(image);
    const a = readIdentify(r);
    setBusy(false);
    if (a.outcome === 'matched') {
      setTest({ kind: 'success', text: `✓ ${a.name}${a.similarity != null ? ` (${similarityText(a.similarity)})` : ''}` });
    } else {
      const label = OUTCOME_LABELS[a.outcome] || (a.outcome === 'error' ? 'No answer' : a.outcome);
      setTest({ kind: 'warning', text: `${label}: ${a.message || r.message || 'nothing recognised.'}` });
    }
    if (r.stationGone) onChanged();
  };

  const stopUsing = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Stop using face sign-in on this computer? It forgets its station key; an Admin can set it up again.')) return;
    clearStation();
    stopCamera();
    setTest(null);
    setMsg({ kind: 'info', text: 'This computer no longer uses face sign-in. (An Admin can revoke the station below.)' });
    onChanged();
  };

  return (
    <Card style={styles.card} data-testid="face-this-computer">
      <Card.Header as="h5">This computer</Card.Header>
      <Card.Body>
        {revokedNote && <Alert variant="warning" className="py-2">{revokedNote}</Alert>}
        {!stationKey ? (
          <Form onSubmit={setUp}>
            <p className="mb-2">Set up this computer as a face sign-in station.</p>
            <p style={styles.muted}>
              Only the shop-floor station computers with a webcam. An Admin PIN is needed; the station key stays in this browser.
            </p>
            <Form.Group className="mb-2" controlId="face-station-name">
              <Form.Label className="small mb-1">Station name</Form.Label>
              <Form.Control
                value={name}
                maxLength={40}
                placeholder="Laser"
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                style={{ maxWidth: 280 }}
              />
              <div style={styles.chips}>
                {STATION_SUGGESTIONS.map((s) => (
                  <Button key={s} size="sm" variant="outline-secondary" onClick={() => setName(s)} disabled={busy}>{s}</Button>
                ))}
              </div>
            </Form.Group>
            <Button type="submit" disabled={busy || !name.trim()}>Set up this computer</Button>
          </Form>
        ) : (
          <div style={styles.row}>
            <div style={styles.col}>
              <div className="d-flex flex-wrap align-items-baseline gap-2 mb-2">
                <strong style={{ fontSize: '1.15rem' }}>{(status && status.name) || getStationName() || 'This station'}</strong>
                {!status && <Badge bg="secondary">Checking…</Badge>}
                {status && status.live && <Badge bg="success">Live</Badge>}
                {status && !status.live && !status.error && status.station && <Badge bg="secondary">Off (switched off in JobQ)</Badge>}
                {status && status.error && <Badge bg="warning" text="dark">No answer from JobQ</Badge>}
              </div>
              {status && status.enrolled_count != null && (
                <div style={styles.muted}>{status.enrolled_count} {status.enrolled_count === 1 ? 'person' : 'people'} enrolled</div>
              )}
              <p style={{ ...styles.muted, marginTop: 8 }}>
                When JobQ asks for a PIN here, the camera looks for the person first; the keypad is always there.
              </p>
              <div className="d-flex flex-wrap gap-2 mt-2">
                <Button variant="primary" onClick={runTest} disabled={busy || !stream}>Test sign-in</Button>
                <Button variant="outline-danger" onClick={stopUsing} disabled={busy}>Stop using face sign-in on this computer</Button>
              </div>
              {test && <Alert variant={test.kind} className="py-2 mt-3 mb-0" data-testid="face-test-result">{test.text}</Alert>}
            </div>
            <div>
              {stream ? <FacePreview stream={stream} width={320} testId="face-station-preview" />
                : <div style={styles.muted}>{cameraError || 'Starting the camera…'}</div>}
            </div>
          </div>
        )}
        {msg && <Alert variant={msg.kind} className="py-2 mt-3 mb-0">{msg.text}</Alert>}
      </Card.Body>
    </Card>
  );
}

function EnrollCard({ stream, cameraError, onChanged }) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const enroll = async (e) => {
    e.preventDefault();
    if (!pin) return;
    setBusy(true);
    setMsg({ kind: 'info', text: 'Hold still and look at the camera…' });
    const images = [];
    for (let i = 0; i < ENROLL_FRAMES; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const img = await captureJpeg();
      if (img) images.push(img);
      // eslint-disable-next-line no-await-in-loop
      if (i < ENROLL_FRAMES - 1) await sleep(ENROLL_GAP_MS);
    }
    if (images.length < 3) {
      setBusy(false);
      setMsg({ kind: 'danger', text: lastCameraError() || 'The camera gave too few pictures. Try again.' });
      return;
    }
    const r = await enrollFace(pin, images);
    setBusy(false);
    setPin('');
    if (r.ok && r.body) {
      setMsg({ kind: 'success', text: `${r.body.name || 'You'} ${r.body.name ? 'is' : 'are'} enrolled (${r.body.faces} face${r.body.faces === 1 ? '' : 's'} on file). JobQ will recognise you at the stations from now on.` });
      onChanged();
      return;
    }
    if (r.stationGone) {
      setMsg({ kind: 'danger', text: r.message || 'This computer is not a face sign-in station any more.' });
      onChanged();
      return;
    }
    const why = r.status === 401 ? (r.message || 'That PIN did not match anyone.') : (r.message || 'JobQ did not answer.');
    setMsg({ kind: 'danger', text: why });
  };

  return (
    <Card style={styles.card} data-testid="face-enroll">
      <Card.Header as="h5">Enroll a person</Card.Header>
      <Card.Body>
        <div style={styles.notice} data-testid="face-notice">{FACE_NOTICE}</div>
        <div style={styles.row}>
          <Form onSubmit={enroll} style={styles.col}>
            <p className="mb-2">The person being enrolled stands in front of the camera and types their own PIN.</p>
            <Form.Group className="mb-2" controlId="face-enroll-pin">
              <Form.Label className="small mb-1">Your PIN</Form.Label>
              <Form.Control
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={pin}
                maxLength={6}
                placeholder="****"
                onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ''))}
                disabled={busy}
                style={styles.pin}
              />
            </Form.Group>
            <Button type="submit" disabled={busy || !pin || !stream}>{busy ? 'Taking pictures…' : 'Enroll my face'}</Button>
            {msg && <Alert variant={msg.kind} className="py-2 mt-3 mb-0" data-testid="face-enroll-result">{msg.text}</Alert>}
          </Form>
          <div>
            {stream ? <FacePreview stream={stream} width={320} testId="face-enroll-preview" />
              : <div style={styles.muted}>{cameraError || 'Starting the camera…'}</div>}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

function RemoveMyFaceCard({ onChanged }) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const remove = async (e) => {
    e.preventDefault();
    if (!pin) return;
    setBusy(true);
    const r = await removeMyFace(pin);
    setBusy(false);
    setPin('');
    if (r.ok) {
      setMsg({ kind: 'success', text: `${(r.body && r.body.name) || 'Your'}${r.body && r.body.name ? "'s" : ''} face was removed. JobQ will ask for the PIN from now on.` });
      onChanged();
    } else {
      if (r.stationGone) onChanged();
      setMsg({ kind: 'danger', text: r.message || 'JobQ did not answer.' });
    }
  };

  return (
    <Card style={styles.card} data-testid="face-remove-mine">
      <Card.Header as="h6">Remove my face</Card.Header>
      <Card.Body>
        <Form onSubmit={remove} className="d-flex flex-wrap align-items-end gap-2">
          <Form.Group controlId="face-remove-pin">
            <Form.Label className="small mb-1">Your PIN</Form.Label>
            <Form.Control
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={pin}
              maxLength={6}
              placeholder="****"
              onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ''))}
              disabled={busy}
              style={styles.pin}
            />
          </Form.Group>
          <Button type="submit" variant="outline-danger" disabled={busy || !pin}>Remove my face</Button>
        </Form>
        {msg && <Alert variant={msg.kind} className="py-2 mt-3 mb-0" data-testid="face-remove-result">{msg.text}</Alert>}
      </Card.Body>
    </Card>
  );
}

function AdminSection({ reloadTick, onStationsChanged }) {
  const admin = useContext(AdminContext);
  const unlocked = Boolean(admin && admin.isAdminActive);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pinNeeded, setPinNeeded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    // One PIN-checked call first: a stale PIN costs one miss on the API's limiter, not three.
    const e = await listEnrollments();
    setPinNeeded(needsAdminPin(e));
    if (!e.ok) {
      setError(e.message);
      setData(null);
      return;
    }
    const [s, m] = await Promise.all([listStations(), faceSummary(7)]);
    setError(s.ok ? null : s.message);
    setData({
      enrollments: (e.body && e.body.enrollments) || [],
      notEnrolled: (e.body && e.body.not_enrolled) || [],
      stations: (s.ok && s.body && s.body.stations) || [],
      summary: m.ok ? m.body : null,
    });
  }, []);

  useEffect(() => {
    if (unlocked) load();
  }, [unlocked, load, reloadTick]);

  const unlock = async () => {
    const user = await requestAdminPin();
    if (!user || !admin || !admin.loginAsAdmin) return;
    admin.loginAsAdmin({ userId: user.userId, userName: user.userName, role: user.role, pin: user.pin }, { remember: user.remember });
    if (unlocked) load();
  };

  const act = async (question, call) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(question)) return;
    setBusy(true);
    const r = await call();
    setBusy(false);
    if (!r.ok) setError(r.message || 'JobQ did not answer.');
    await load();
  };

  if (!unlocked) {
    return (
      <Card style={styles.card} data-testid="face-admin">
        <Card.Header as="h5">Admin</Card.Header>
        <Card.Body>
          <p className="mb-2">Who is enrolled, the station computers, and the last 7 days need an Admin PIN.</p>
          <p style={styles.muted}>
            The unlock lasts for this tab. On a station computer, Clear Credentials when you are done: while a tab is
            unlocked, JobQ fills in the Admin PIN instead of looking for faces.
          </p>
          <Button variant="outline-primary" onClick={unlock}>Enter Admin PIN</Button>
        </Card.Body>
      </Card>
    );
  }

  const summary = data && data.summary;
  return (
    <Card style={styles.card} data-testid="face-admin">
      <Card.Header as="h5">Admin</Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" className="d-flex flex-wrap align-items-center gap-2 py-2">
            <span>{pinNeeded ? `This needs an Admin PIN. ${error}` : error}</span>
            {pinNeeded && <Button size="sm" variant="outline-danger" onClick={unlock}>Enter Admin PIN</Button>}
          </Alert>
        )}
        {!data && !error && <div style={styles.muted}>Loading…</div>}

        {summary && (
          <div className="mb-3" data-testid="face-summary">
            <h6>Last {summary.days || 7} days</h6>
            <div style={styles.small}>{outcomeLine(summary.by_outcome) || 'No sign-in attempts.'}</div>
            <div style={styles.muted}>
              “Not you?” {summary.not_me || 0}
              {summary.median_ms != null && <> · median {(summary.median_ms / 1000).toFixed(1)} s per attempt</>}
            </div>
          </div>
        )}

        {data && (
          <>
            <h6>Enrolled ({data.enrollments.length})</h6>
            <Table size="sm" responsive striped className="mb-2" data-testid="face-enrollments">
              <thead>
                <tr><th>Name</th><th>Enrolled</th><th>Faces</th><th>Last match</th><th>Matches 30d</th><th>Not me 30d</th><th /></tr>
              </thead>
              <tbody>
                {data.enrollments.length === 0 && <tr><td colSpan={7} style={styles.muted}>Nobody yet.</td></tr>}
                {data.enrollments.map((p) => (
                  <tr key={p.user_id}>
                    <td>{p.name}</td>
                    <td>{stamp(p.enrolled_at)}</td>
                    <td>{p.faces}</td>
                    <td>{stamp(p.last_matched_at)}</td>
                    <td>{p.matches_30d || 0}</td>
                    <td>{p.not_me_30d || 0}</td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={busy}
                        onClick={() => act(`Remove ${p.name}'s face? They will type their PIN until they enroll again.`, () => removeEnrollment(p.user_id))}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {data.notEnrolled.length > 0 && (
              <div className="mb-3" style={styles.small} data-testid="face-not-enrolled">
                <span style={styles.muted}>Not enrolled: </span>
                {data.notEnrolled.map((p) => p.name).join(', ')}
              </div>
            )}

            <h6 className="mt-3">Stations</h6>
            <Table size="sm" responsive striped data-testid="face-stations">
              <thead>
                <tr><th>Name</th><th>Created by</th><th>Last seen</th><th>Identifies 7d</th><th>Matches 7d</th><th /></tr>
              </thead>
              <tbody>
                {data.stations.length === 0 && <tr><td colSpan={6} style={styles.muted}>No stations yet.</td></tr>}
                {data.stations.map((s) => (
                  <tr key={s.station_id} style={s.revoked_at ? { opacity: 0.55 } : undefined}>
                    <td>{s.name}{s.revoked_at && <Badge bg="secondary" className="ms-2">Revoked {stamp(s.revoked_at)}</Badge>}</td>
                    <td>{s.created_by_name || '—'}<div style={styles.muted}>{stamp(s.created_at)}</div></td>
                    <td>{stamp(s.last_seen_at)}</td>
                    <td>{s.identifies_7d || 0}</td>
                    <td>{s.matched_7d || 0}</td>
                    <td className="text-end">
                      {!s.revoked_at && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={busy}
                          onClick={() => act(`Revoke the "${s.name}" station? That computer stops using face sign-in at once.`, async () => {
                            const r = await revokeStation(s.station_id);
                            if (r.ok) onStationsChanged();
                            return r;
                          })}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {summary && Array.isArray(summary.by_station) && summary.by_station.length > 0 && (
              <div style={styles.small}>
                {summary.by_station.map((st) => (
                  <div key={st.station_id}><strong>{st.name}:</strong> {outcomeLine(st.by_outcome) || 'no attempts'}</div>
                ))}
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default function FaceSignInPage() {
  const [stationKey, setStationKey] = useState(() => getStationKey());
  const [status, setStatus] = useState(null);
  const [revokedNote, setRevokedNote] = useState(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(async () => {
    const key = getStationKey();
    setStationKey(key);
    if (!key) {
      setStatus(null);
      return;
    }
    const st = await getStationStatus({ force: true });
    if (st.station) setRevokedNote(null);
    if (st.revoked) setRevokedNote(`JobQ no longer knows this computer as a station${st.message ? ` (${st.message})` : ''}. Set it up again to use face sign-in here.`);
    setStationKey(getStationKey());
    setStatus(st.station ? st : null);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const changed = useCallback(() => {
    refresh();
    setTick((t) => t + 1);
  }, [refresh]);

  const isStation = Boolean(stationKey);
  const { stream, error: cameraError } = useStationCamera(isStation);

  return (
    <div className="container py-4" style={styles.page}>
      <h2 className="mb-1">Face sign-in</h2>
      <p className="text-muted">
        At the shop-floor station computers, JobQ can recognise the person at the camera when it asks for a PIN.
        The PIN keypad always stays as the fallback.
      </p>
      <ThisComputerCard
        stationKey={stationKey}
        status={status}
        stream={stream}
        cameraError={cameraError}
        onChanged={changed}
        revokedNote={revokedNote}
      />
      {isStation && (
        <>
          <EnrollCard stream={stream} cameraError={cameraError} onChanged={changed} />
          <RemoveMyFaceCard onChanged={changed} />
        </>
      )}
      <AdminSection reloadTick={tick} onStationsChanged={refresh} />
    </div>
  );
}
